import React, { useState, useEffect } from 'react';

import {
  Box,
  Button,
  Divider,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  styled,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  List,
  ListItem,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { spliceString } from '../../../utils/formatString';

import { mergeSchema, checkValueExists } from '../../../utils/dataSchema';
import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';

import { useRegions, regionTypes } from '../../../hooks/api/useRegions';
import {
  initialValues,
  initialPasswordValues,
  validationSchema,
  validationPasswordSchema,
} from '../../../schema/users/retailerSchema';
import { useBranches, branchTypes, targetTypes as outletTargetTypes } from '../../../hooks/api/useBranches';
import { useBusiness, businessTypes, targetTypes as businessTargetTypes } from '../../../hooks/api/useBusiness';
import {
  initialValues as initialOutletValues,
  validationSchema as validationOutletSchema,
  retailTypes,
} from '../../../schema/users/branchSchema';
import { inputTypes, inputTypesDef } from '../../../schema/customFields/customFieldSchema';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from '../../common/CommonSectionHeader';
import CommonRestricted from '../../common/CommonRestricted';

import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import { useStateCustomFields } from '../../../hooks/state/useStateCustomFields';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default function NewRetailer({ routeType }) {
  const { t } = useTranslations({ ns: ['common', '@user'] });

  const { promptMessage } = useSystemPrompt();
  const { create: createUser } = useUsers({ userType: userTypes.retail, targetType: targetTypes.retail });
  const { list: listCustomFields } = useCustomFields({
    customFieldModule: customFieldModules.user,
    customFieldFunction: customFieldFunctions.user.retailer,
  });

  const { create: createOutlet } = useBranches({
    branchType: branchTypes.retail,
    targetType: outletTargetTypes.retail,
  });
  const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.retail.id);

  const {
    dataCustomFields,

    getInitialFormStateRows,
    getSubmitFormDataFromRows,

    getCustomFieldOptionNameByIds,

    RenderCustomFieldSelectionTextbox,
    RenderCustomFieldSelectionDropdown,
    RenderCustomFieldSelectionMultiDropdown,
  } = useStateCustomFields({
    fieldModule: customFieldModules.user,
    moduleFunction: customFieldFunctions.user.retailer,
  });

  const { list: listBusiness, filterOperatorTypes } = useBusiness({ businessType: businessTypes.business });
  const navigate = useNavigate();
  const { setSystemLoading } = useAppContextFeatures();
  const { getRoute } = useSystemRoutes(routeType);
  const { getHtmlHead } = useSystemHead(routeType);

  const { list: listRegions } = useRegions({});
  const [dataCountries, setDataCountries] = useState([]);
  const [dataStates, setDataStates] = useState([]);
  const [dataAreas, setDataAreas] = useState([]);
  const [dataBusiness, setDataBusiness] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const validationSchemaCombine = mergeSchema(validationSchema, validationPasswordSchema, validationOutletSchema);

  const initialValuesCombine = {
    ...initialValues,
    ...initialPasswordValues,
    ...initialOutletValues,
  };

  const onSubmitOutlet = async (id, currentValues) => {
    try {
      let data = {
        branch: {
          branch_name: currentValues?.branchName,
          branch_code: currentValues?.branchCode,
          branch_contact: currentValues?.branchContact,
          branch_email: currentValues?.branchEmail,
          type: currentValues?.type,
          address_1: currentValues?.address1,
          address_2: currentValues?.address2,
          country_id: currentValues?.countryId,
          state_id: currentValues?.stateId,
          area_id: currentValues?.areaId,
          postcode: currentValues?.postcode,
          remark: currentValues?.branchRemark,
          pic_name: currentValues?.picName,
          pic_contact: currentValues?.picContact,
          pic_email: currentValues?.picEmail,
          is_hq: currentValues?.isHQ ? 1 : 0,
        },
      };

      // return await createOutlet({ id, data });
      const response = await createOutlet({ id, data });

      // promptMessage(true, `${response?.message}`)
      // navigate(getRoute(`users/retailer/${id}/retailerOutlets`))
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }

    return true;
  };

  const onSubmitRetailer = async (currentValues) => {
    try {
      const { rows } = values;

      let cf = getSubmitFormDataFromRows({ rows });

      let data = {
        credential: {
          username: currentValues?.username,
          password: currentValues?.password,
          user_type: currentValues?.userTypes,
          firstname: currentValues?.firstname,
          lastname: currentValues?.lastname,
          contact: currentValues?.contactno,
          email: currentValues?.email,
          status: currentValues?.status ? 1 : 0,
        },
        props: {
          business_id: currentValues?.businessId ? currentValues.businessId : '',
        },
        retailer: {
          code: currentValues?.code,
          account_code: currentValues?.accountcode,
          company_name: currentValues?.companyname,
          reg_no: currentValues?.regno,
          remark: currentValues?.remark,
        },
        status: {
          is_different_business: currentValues?.differentBusiness ? 1 : 0,
        },
        custom_fields: cf,
      };

      if (cf.length) {
        data.custom_fields = cf;
      }

      return await createUser({ data });
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }

    return true;
  };

  const onSubmit = async (currentValues) => {
    setSystemLoading(true);

    try {
      const response = await onSubmitRetailer(currentValues);

      const { user } = response?.data ? response.data : {};

      if (user?.id) await onSubmitOutlet(user?.id, currentValues);

      promptMessage(true, `${response?.message}`);

      navigate(getRoute('users/retailers'));
    } catch (error) {
      promptMessage(false, `Action Failed. (${error?.message})`);
    }

    setSystemLoading(false);
  };

  const loadInitialCustomFieldState = async () => {
    const cf = await getInitialFormStateRows({
      savedCustomFields: [],
    });

    setFieldValue(`rows`, cf);
  };

  // const fetchCustomFields = async ({ fieldModule, moduleFunction }) => {

  //     setSystemLoading(true)

  //     try {

  //         const props = {
  //             limit: -1,
  //             skip: 0,
  //             sortInfo: {
  //                 id: "id",
  //                 dir: 0,
  //             },
  //             filterValue: [
  //                 {
  //                     name: "field_module",
  //                     operator: filterOperatorTypes.eq,
  //                     value: fieldModule
  //                 },
  //                 {
  //                     name: "module_function",
  //                     operator: filterOperatorTypes.eq,
  //                     value: moduleFunction
  //                 }
  //             ]
  //         };

  //         const response = await listCustomFields(props);

  //         const _cf = response?.data?.custom_fields

  //         setCustomFields(_cf)

  //         // response?.data?.custom_fields.forEach((e, index) => {
  //         //     setFieldValue(`rows.${index}.customFieldId`, e.id)
  //         //     setFieldValue(`rows.${index}.customValue`, "")
  //         //     setFieldValue(`rows.${index}.customArray`, [])
  //         // })

  //         const cf = getInitialFormStateRows({
  //             savedCustomFields: [],
  //             customFields: _cf
  //         })

  //         setFieldValue(`rows`, cf)

  //         // promptMessage(true, `${response?.message}`)

  //     } catch (error) {
  //         promptMessage(false, `Action Failed (${error?.message})`)
  //     }

  //     setSystemLoading(false)

  // }

  const fetchDataCountry = async () => {
    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'country_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.country;

      const { data } = await listRegions(props);

      setDataCountries(data?.countries);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataStates = async (countryId) => {
    setDataStates([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'state_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.state;
      props.countryId = countryId;

      const { data } = await listRegions(props);

      setDataStates(data?.states);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataAreas = async (stateId) => {
    setDataAreas([]);

    try {
      const props = {
        limit: -1,
        offset: 0,
        filterValue: [],
        sortInfo: {
          id: 'area_name',
          dir: 0,
        },
      };

      props.regionType = regionTypes.area;
      props.stateId = stateId;

      const { data } = await listRegions(props);

      setDataAreas(data?.areas);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const fetchDataBusiness = async () => {
    let filterValues = [];

    try {
      filterValues.push({
        name: 'status',
        operator: filterOperatorTypes.eq,
        value: '1',
      });

      const props = {
        limit: -1,
        offset: 0,
        filterValue: filterValues,
        sortInfo: {
          id: 'company_name',
          dir: 0,
        },
      };

      const { data } = await listBusiness(props);

      setDataBusiness(data?.businesses);
    } catch (error) {
      promptMessage(false, `${error?.message}`);
    }
  };

  const formik = useFormik({
    initialValues: initialValuesCombine,
    validationSchema: validationSchemaCombine,
    enableReinitialize: true,
    onSubmit,
  });

  const { values, errors, touched, handleChange, setFieldValue } = formik;

  useEffect(() => {
    fetchDataCountry();
    fetchDataBusiness();
    loadInitialCustomFieldState();

    setFieldValue(`type`, retailTypes.filter((i) => i.default)[0].id);
    setFieldValue(`isHQ`, true);
    setFieldValue(`status`, true);
  }, []);

  useEffect(() => {
    if (formik?.values?.countryId) {
      if (dataStates.length) {
        if (dataStates[0].country_id !== formik.values.countryId) {
          fetchDataStates(formik.values.countryId);
        }
      } else {
        fetchDataStates(formik.values.countryId);
      }
    }

    if (formik?.values?.stateId) {
      if (dataAreas.length) {
        if (dataAreas[0].state_id !== formik.values.stateId) {
          fetchDataAreas(formik.values.stateId);
        }
      } else {
        fetchDataAreas(formik.values.stateId);
      }
    }
  }, [formik.values]);

  const checkPostcodeExists = () => {
    const checkExists = getPostcodesArray().find((i) => i === values.postcode);

    if (checkExists) return values.postcode;

    return '';
  };

  const getPostcodesArray = () => {
    let postcodeStringCheck = dataAreas.filter((i) => i.id === formik.values.areaId);

    if (postcodeStringCheck.length <= 0) return [];

    let postcodeString = postcodeStringCheck.length >= 0 ? postcodeStringCheck[0].postcode_string : '';

    let postcodeArray = postcodeString.includes(',') ? postcodeString.split(',') : [postcodeString];

    if (postcodeArray.length <= 0) return [];

    return postcodeArray;
  };

  const RenderCustomFieldSelection = () => {
    if (!values?.rows) return <></>;

    const rows = values.rows;

    return (
      <>
        <Grid item xs={12}>
          <Divider sx={{ mt: 2, mb: 1 }} />
        </Grid>

        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('customProperties')} subTitle={t('provideCustomItemInformation')} />
        </Grid>
        <Grid item xs={12} sm={12} md={9}>
          <List>
            {rows.map((item, index) => {
              let itemValues = item;

              let { fieldType, fieldLabel, fieldKey } = item;

              return (
                <ListItem
                  key={index}
                  divider
                  disablePadding
                  sx={{
                    py: 1,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid
                      item
                      md={3}
                      xs={12}
                      sm={12}
                      display={'flex'}
                      flexDirection={'column'}
                      justifyContent={'center'}
                      alignItems={'center'}
                    >
                      <Chip
                        label={fieldLabel}
                        color="primary"
                        variant="outlined"
                        size="large"
                        // avatar={<CommonIcons name={icons.add} />}
                        sx={{
                          width: '100%',
                          // textAlign: 'left',
                          fontSize: 16,
                          fontWeight: 800,
                        }}
                      />
                    </Grid>
                    <Grid item md={9} xs={12} sm={12}>
                      {fieldType === inputTypesDef.textbox &&
                        RenderCustomFieldSelectionTextbox({
                          index,
                          item,
                          itemValues,
                          inputProps: {
                            fullWidth: true,
                            name: `rows.${index}.customValue`,
                            label: itemValues?.fieldLabel,
                            type: 'text',
                            variant: 'outlined',
                            value: itemValues?.customValue,
                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                          },
                        })}

                      {fieldType === inputTypesDef.dropdown &&
                        RenderCustomFieldSelectionDropdown({
                          index,
                          item,
                          itemValues,
                          selectProps: {
                            labelId: 'customValue',
                            label: itemValues?.fieldLabel,
                            name: `rows.${index}.customArray`,
                            onChange: (event) => {
                              setFieldValue(`rows.${index}.customArray`, [event.target.value]);
                            },
                            size: 'small',
                            margin: 'dense',
                            value: itemValues.customArray.length ? itemValues.customArray[0] : '',
                          },
                        })}

                      {fieldType === inputTypesDef.multidropdown &&
                        RenderCustomFieldSelectionMultiDropdown({
                          index,
                          item,
                          itemValues,
                          selectProps: {
                            labelId: itemValues?.fieldLabel,
                            label: itemValues?.fieldLabel,
                            name: `rows.${index}.customArray`,
                            onChange: handleChange,
                            renderValue: (selected) => {
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map(
                                    (i, index) => (
                                      <Chip
                                        key={index}
                                        // color={'red'}
                                        label={spliceString({ val: i, maxLength: 20 })}
                                      />
                                    )
                                  )}
                                </Box>
                              );
                            },
                            size: 'small',
                            margin: 'dense',
                            multiple: true,
                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                            value: itemValues?.customArray ? itemValues.customArray : [],
                          },
                        })}
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </Grid>
      </>
    );
  };

  const RenderFormAuth = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('credential')} subTitle={t('accessAuthentication')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                autoFocus
                fullWidth
                name={'username'}
                label={t('username')}
                type="text"
                variant="outlined"
                value={values.username}
                onChange={handleChange}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
              />
            </Grid>
            {/* <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status" 
                                        checked={Boolean(values.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                } 
                                label={
                                    Boolean(values.status)? "Enabled" : "Disabled"
                                }
                            />
                        </Grid> */}

            <Grid item xs={12} md={6}>
              <></>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'password'}
                label={t('password')}
                type="password"
                variant="outlined"
                value={values.password}
                onChange={handleChange}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'passwordconfirm'}
                label={t('confirmPassword')}
                type="password"
                variant="outlined"
                value={values.passwordconfirm}
                onChange={handleChange}
                error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                helperText={touched.passwordconfirm && errors.passwordconfirm}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderFormProfile = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('profile')} subTitle={t('fullDetailsOfCustomerInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    sx={{
                      ml: 2,
                    }}
                    name="differentBusiness"
                    checked={Boolean(values.differentBusiness)}
                    onChange={(event, checked) => {
                      setFieldValue('differentBusiness', checked);
                    }}
                  />
                }
                label={Boolean(values.differentBusiness) ? t('differentBusiness') : t('sameBusiness')}
              />
            </Grid>

            {values.differentBusiness === true ? (
              <Grid item xs={12} sm={12} md={6}>
                <FormControl
                  margin="dense"
                  fullWidth
                  size="small"
                  error={touched.businessId && Boolean(errors.businessId)}
                >
                  <InputLabel id="businessId">{t('business')}</InputLabel>
                  <Select
                    disabled={values?.differentBusiness === false}
                    labelId="businessId"
                    label={t('business')}
                    name={'businessId'}
                    onChange={(event) => {
                      setFieldValue('businessId', event.target.value);
                    }}
                    value={values.businessId}
                    sx={{
                      textAlign: 'left',
                    }}
                  >
                    {dataBusiness.map((i, index) => {
                      return (
                        <MenuItem key={index} value={i.id}>
                          {i.company_name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>{touched.businessId && errors.businessId}</FormHelperText>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} sm={12} md={6}>
                <></>
              </Grid>
            )}

            {/* <Grid item xs={12} sm={6} md={6}>
                            <></>
                        </Grid> */}

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'firstname'}
                label={t('firstName')}
                type="text"
                variant="outlined"
                value={values.firstname}
                onChange={handleChange}
                error={touched.firstname && Boolean(errors.firstname)}
                helperText={touched.firstname && errors.firstname}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'lastname'}
                label={t('lastName')}
                type="text"
                variant="outlined"
                value={values.lastname}
                onChange={handleChange}
                error={touched.lastname && Boolean(errors.lastname)}
                helperText={touched.lastname && errors.lastname}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'companyname'}
                label={t('companyName')}
                type="text"
                variant="outlined"
                value={values.companyname}
                onChange={handleChange}
                error={touched.companyname && Boolean(errors.companyname)}
                helperText={touched.companyname && errors.companyname}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'regno'}
                label={t('regNo')}
                type="text"
                variant="outlined"
                value={values.regno}
                onChange={handleChange}
                error={touched.regno && Boolean(errors.regno)}
                helperText={touched.regno && errors.regno}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'code'}
                label={t('code')}
                type="text"
                variant="outlined"
                value={values.code}
                onChange={handleChange}
                error={touched.code && Boolean(errors.code)}
                helperText={touched.code && errors.code}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'accountcode'}
                label={t('accountCode')}
                type="text"
                variant="outlined"
                value={values.accountcode}
                onChange={handleChange}
                error={touched.accountcode && Boolean(errors.accountcode)}
                helperText={touched.accountcode && errors.accountcode}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'email'}
                label={t('email')}
                type="email"
                variant="outlined"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'contactno'}
                label={t('contactNo')}
                type="text"
                variant="outlined"
                value={values.contactno}
                onChange={handleChange}
                error={touched.contactno && Boolean(errors.contactno)}
                helperText={touched.contactno && errors.contactno}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'remark'}
                label={t('remark')}
                type="text"
                variant="outlined"
                value={values.remark}
                onChange={handleChange}
                error={touched.remark && Boolean(errors.remark)}
                helperText={touched.remark && errors.remark}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderFormOutletProfile = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('mainOutletInfo')} subTitle={t('fullDetailsOfOutletInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            {/* <Grid item xs={12} md={12} textAlign="left">
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        name="isHQ" 
                                        checked={Boolean(values.isHQ)}
                                        onChange={(event, checked) => {
                                            setFieldValue("isHQ", checked);
                                        }}
                                    />
                                } 
                                label={
                                    Boolean(values.isHQ)? "Is HQ" : "No HQ"
                                }
                            />
                        </Grid> */}

            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'branchName'}
                label={t('outletName')}
                type="text"
                variant="outlined"
                value={values.branchName}
                onChange={handleChange}
                error={touched.branchName && Boolean(errors.branchName)}
                helperText={touched.branchName && errors.branchName}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'branchCode'}
                label={t('outletCode')}
                type="text"
                variant="outlined"
                value={values.branchCode}
                onChange={handleChange}
                error={touched.branchCode && Boolean(errors.branchCode)}
                helperText={touched.branchCode && errors.branchCode}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'branchContact'}
                label={t('outletContact')}
                type="text"
                variant="outlined"
                value={values.branchContact}
                onChange={handleChange}
                error={touched.branchContact && Boolean(errors.branchContact)}
                helperText={touched.branchContact && errors.branchContact}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'branchEmail'}
                label={t('outletEmail')}
                type="text"
                variant="outlined"
                value={values.branchEmail}
                onChange={handleChange}
                error={touched.branchEmail && Boolean(errors.branchEmail)}
                helperText={touched.branchEmail && errors.branchEmail}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'picName'}
                label={t('contactName')}
                type="text"
                variant="outlined"
                value={values.picName}
                onChange={handleChange}
                error={touched.picName && Boolean(errors.picName)}
                helperText={touched.picName && errors.picName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'picContact'}
                label={t('contactNo')}
                type="text"
                variant="outlined"
                value={values.picContact}
                onChange={handleChange}
                error={touched.picContact && Boolean(errors.picContact)}
                helperText={touched.picContact && errors.picContact}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'picEmail'}
                label={t('contactEmail')}
                type="text"
                variant="outlined"
                value={values.picEmail}
                onChange={handleChange}
                error={touched.picEmail && Boolean(errors.picEmail)}
                helperText={touched.picEmail && errors.picEmail}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'branchRemark'}
                label={t('remark')}
                type="text"
                variant="outlined"
                value={values.branchRemark}
                onChange={handleChange}
                error={touched.branchRemark && Boolean(errors.branchRemark)}
                helperText={touched.branchRemark && errors.branchRemark}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderFormOutletAdditional = () => {
    return (
      <>
        <Grid item xs={12} sm={12} md={3} textAlign={'left'}>
          <CommonSectionHeader title={t('mainOutletAddress')} subTitle={t('fullOutletAddressDetailsInformation')} />
        </Grid>

        <Grid item xs={12} sm={12} md={9}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.type && Boolean(errors.type)}>
                <InputLabel id="type">{t('outletType')}</InputLabel>
                <Select
                  disabled
                  labelId="type"
                  label={t('outletType')}
                  name={'type'}
                  // onChange={(event) =>{
                  //         setFieldValue("type",event.target.value)
                  //     }
                  // }
                  value={values.type}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {retailTypes.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.display}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.type && errors.type}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <></>
            </Grid>

            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address1'}
                label={`${t('address')} 1`}
                type="text"
                variant="outlined"
                value={values.address1}
                onChange={handleChange}
                error={touched.address1 && Boolean(errors.address1)}
                helperText={touched.address1 && errors.address1}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                size="small"
                fullWidth
                name={'address2'}
                label={`${t('address')} 2`}
                type="text"
                variant="outlined"
                value={values.address2}
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
                helperText={touched.address2 && errors.address2}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.countryId && Boolean(errors.countryId)}>
                <InputLabel id="country">{t('country')}</InputLabel>
                <Select
                  labelId="countryId"
                  label={t('country')}
                  name={'countryId'}
                  onChange={(event) => {
                    setFieldValue(`countryId`, event.target.value);
                    setFieldValue(`stateId`, '');
                    setFieldValue(`areaId`, '');
                    setDataStates([]);
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataCountries,
                    valueToCompare: values.countryId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataCountries.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.country_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.countryId && errors.countryId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.stateId && Boolean(errors.stateId)}>
                <InputLabel id="state">{t('state')}</InputLabel>
                <Select
                  labelId="stateId"
                  label={t('state')}
                  name={'stateId'}
                  onChange={(event) => {
                    setFieldValue(`stateId`, event.target.value);
                    setFieldValue(`areaId`, '');
                    setDataAreas([]);
                  }}
                  value={checkValueExists({
                    dataSearch: dataStates,
                    valueToCompare: values.stateId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataStates.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.state_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.stateId && errors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.areaId && Boolean(errors.areaId)}>
                <InputLabel id="area">{t('area')}</InputLabel>
                <Select
                  labelId="areaId"
                  label={t('area')}
                  name={'areaId'}
                  onChange={handleChange}
                  value={checkValueExists({
                    dataSearch: dataAreas,
                    valueToCompare: values.areaId,
                  })}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {dataAreas.map((i, index) => {
                    return (
                      <MenuItem key={index} value={i.id}>
                        {i.area_name}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{touched.areaId && errors.areaId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl margin="dense" fullWidth size="small" error={touched.postcode && Boolean(errors.postcode)}>
                <InputLabel id="postcode">{t('postcode')}</InputLabel>
                <Select
                  labelId="postcode"
                  label={t('postcode')}
                  name={'postcode'}
                  onChange={handleChange}
                  // value={
                  //     values.postcode
                  // }
                  value={checkPostcodeExists()}
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {getPostcodesArray().length ? (
                    getPostcodesArray().map((i, index) => {
                      return (
                        <MenuItem key={index} value={i}>
                          {i}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem value={''}>{null}</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.postcode && errors.postcode}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const RenderForm = () => {
    return (
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={1}>
            {RenderFormAuth()}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {RenderFormProfile()}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {RenderFormOutletProfile()}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {RenderFormOutletAdditional()}

            {RenderCustomFieldSelection()}
          </Grid>
        </StyledCard>
        <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
          <Grid item md={12} textAlign={'right'}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
              }}
              // loading={isLoading}
            >
              {t('saveChanges')}
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <Box>
      {getHtmlHead({ routeType, title: headTitles.newRetailer })}

      <CommonBreadcumbs
        routeType={routeType}
        pageTitle={t('newRetailer')}
        breadcrumbs={[
          {
            displayText: t('retailers'),
            props: {
              href: getRoute(`users/retailers`),
            },
          },
          {
            displayText: t('newRetailer'),
            props: {
              color: 'secondary.main',
            },
          },
        ]}
      />

      {isPermissionAllow({ functionType: functionTypes.pageNew.id }) ? RenderForm() : <CommonRestricted />}
    </Box>
  );
}
